/* PLTX2 Video Feature — click-to-play long-form video with a custom HUD
   (branded play/pause, lavender seek bar, time, mute, fullscreen) instead
   of the native controls.
   - Overlay play button starts playback unmuted.
   - HUD auto-hides after 2.5s idle while playing; any pointer/touch wakes it.
   - Clicking the video toggles play/pause once started.
   - Pauses itself when scrolled out of view; overlay returns on ended. */
(() => {
  const HIDE_DELAY = 2500;

  const formatTime = (seconds) => {
    if (!isFinite(seconds)) return '0:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return m + ':' + String(s).padStart(2, '0');
  };

  class Pltx2VideoFeature extends HTMLElement {
    #io = null;
    #hideTimer = null;

    connectedCallback() {
      this.video = this.querySelector('.pltx2-vf__video');
      this.playButton = this.querySelector('.pltx2-vf__play');
      this.frame = this.querySelector('.pltx2-vf__frame');
      this.hud = this.querySelector('.pltx2-vf__hud');
      this.seek = this.querySelector('.pltx2-vf__hud-seek');
      this.timeEl = this.querySelector('.pltx2-vf__hud-time');
      if (!this.video || !this.playButton) return;

      this.playButton.addEventListener('click', () => this.#start());
      this.video.addEventListener('click', () => {
        if (this.classList.contains('is-playing')) this.#togglePlay();
      });

      this.video.addEventListener('play', () => {
        this.classList.remove('is-paused');
        this.#wake();
      });
      this.video.addEventListener('pause', () => {
        this.classList.add('is-paused');
        this.#showHud(); // stay visible while paused
      });
      this.video.addEventListener('ended', () => {
        this.classList.remove('is-playing', 'is-paused');
        this.#hideHud();
      });
      this.video.addEventListener('volumechange', () => {
        this.classList.toggle('is-muted', this.video.muted);
      });

      this.video.addEventListener('loadedmetadata', () => this.#syncTime());
      this.video.addEventListener('timeupdate', () => this.#syncTime());

      if (this.hud) {
        this.querySelector('.pltx2-vf__hud-play')?.addEventListener('click', () => this.#togglePlay());
        this.querySelector('.pltx2-vf__hud-mute')?.addEventListener('click', () => {
          this.video.muted = !this.video.muted;
          this.#wake();
        });
        this.querySelector('.pltx2-vf__hud-fs')?.addEventListener('click', () => this.#toggleFullscreen());
        this.seek?.addEventListener('input', () => {
          if (isFinite(this.video.duration)) {
            this.video.currentTime = (this.seek.value / 100) * this.video.duration;
          }
          this.#wake();
        });
        for (const type of ['pointermove', 'touchstart']) {
          this.frame.addEventListener(type, () => this.#wake(), { passive: true });
        }
      }

      this.#io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting && !this.video.paused) this.video.pause();
          }
        },
        { threshold: 0.2 }
      );
      this.#io.observe(this);
    }

    disconnectedCallback() {
      this.#io?.disconnect();
      this.#io = null;
      if (this.#hideTimer) clearTimeout(this.#hideTimer);
      this.video?.pause();
    }

    #start() {
      this.classList.add('is-playing');
      this.video.muted = false;
      this.video.play().catch(() => {
        // sound-on play rejected (shouldn't happen on a user gesture) —
        // fall back to muted so the click still starts playback
        this.video.muted = true;
        this.video.play().catch(() => {});
      });
    }

    #togglePlay() {
      if (this.video.paused) this.video.play().catch(() => {});
      else this.video.pause();
    }

    #toggleFullscreen() {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (this.frame.requestFullscreen) {
        this.frame.requestFullscreen();
      } else if (this.video.webkitEnterFullscreen) {
        // iPhone Safari: element fullscreen unsupported — use the video's
        this.video.webkitEnterFullscreen();
      }
      this.#wake();
    }

    #syncTime() {
      const { currentTime, duration } = this.video;
      if (this.seek && isFinite(duration) && duration > 0) {
        const p = (currentTime / duration) * 100;
        this.seek.value = p;
        this.seek.style.setProperty('--pltx2-vf-p', p + '%');
      }
      if (this.timeEl) {
        this.timeEl.textContent = formatTime(currentTime) + ' / ' + formatTime(duration);
      }
    }

    #showHud() {
      if (this.#hideTimer) clearTimeout(this.#hideTimer);
      this.#hideTimer = null;
      this.hud?.classList.add('is-visible');
    }

    #hideHud() {
      if (this.#hideTimer) clearTimeout(this.#hideTimer);
      this.#hideTimer = null;
      this.hud?.classList.remove('is-visible');
    }

    /* show the HUD, then re-arm the idle hide (only while playing) */
    #wake() {
      this.#showHud();
      if (!this.video.paused) {
        this.#hideTimer = setTimeout(() => this.hud?.classList.remove('is-visible'), HIDE_DELAY);
      }
    }
  }

  if (!customElements.get('pltx2-video-feature')) {
    customElements.define('pltx2-video-feature', Pltx2VideoFeature);
  }
})();
